/**
 * Product Form — intercepts add-to-cart submissions.
 * Sends `sections` param so the cart drawer can morph without a 2nd fetch.
 */
import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class ProductForm extends Component {
  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector('form[action*="/cart/add"]');
    if (form) form.addEventListener('submit', this.#onSubmit);

    // Clamp qty input on manual typing (prevent 0 or negative)
    this.querySelector('[data-qty-input]')?.addEventListener('input', this.#onQtyInput);
    this.querySelector('[data-qty-input]')?.addEventListener('blur', this.#onQtyBlur);

    // Listen for preferred store selection (store-availability block)
    document.addEventListener('store-availability:preferred', this.#onPreferredStore);
    this.#initPreferredStore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const form = this.querySelector('form[action*="/cart/add"]');
    if (form) form.removeEventListener('submit', this.#onSubmit);

    this.querySelector('[data-qty-input]')?.removeEventListener('input', this.#onQtyInput);
    this.querySelector('[data-qty-input]')?.removeEventListener('blur', this.#onQtyBlur);
    document.removeEventListener('store-availability:preferred', this.#onPreferredStore);
  }

  /** Called via on:click="/onQtyMinus" delegation */
  onQtyMinus() {
    const input = this.querySelector('[data-qty-input]');
    if (!input) return;
    const current = parseInt(input.value, 10) || 1;
    input.value = Math.max(1, current - 1);
  }

  /** Called via on:click="/onQtyPlus" delegation */
  onQtyPlus() {
    const input = this.querySelector('[data-qty-input]');
    if (!input) return;
    const current = parseInt(input.value, 10) || 1;
    input.value = current + 1;
  }

  #onQtyInput = (e) => {
    const val = parseInt(e.target.value, 10);
    if (val < 1) e.target.value = 1;
  };

  #onQtyBlur = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!val || val < 1) e.target.value = 1;
  };

  #onPreferredStore = (event) => {
    const input = this.querySelector('[data-preferred-store-input]');
    if (!input) return;
    const { storeName } = event.detail;
    if (storeName) {
      input.value = storeName;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }
  };

  #initPreferredStore() {
    const storeId = localStorage.getItem('tw_preferred_store_id');
    if (!storeId) return;
    // store-availability fires on connect; wait for it if product-form loads first.
  }

  #onSubmit = async (event) => {
    event.preventDefault();

    const form = event.target;
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
    }

    try {
      // Get the cart-drawer section ID for the sections param
      const cartDrawer = document.querySelector('cart-drawer-component');
      const sectionId = cartDrawer?.dataset.sectionId || 'cart-drawer';

      const formData = new FormData(form);

      // Build JSON body with sections param
      const items = [{
        id: parseInt(formData.get('id'), 10),
        quantity: parseInt(formData.get('quantity') || '1', 10),
      }];

      // Add selling plan if present
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) items[0].selling_plan = parseInt(sellingPlan, 10);

      // Add properties if present
      const properties = {};
      for (const [key, val] of formData.entries()) {
        if (key.startsWith('properties[')) {
          const propName = key.replace('properties[', '').replace(']', '');
          properties[propName] = val;
        }
      }
      if (Object.keys(properties).length) items[0].properties = properties;

      const body = JSON.stringify({
        items,
        sections: sectionId,
        sections_url: window.location.pathname,
      });

      const res = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await res.json();

      if (!res.ok || data.errors) {
        const msg = data.description || data.errors || 'Could not add to cart';
        document.dispatchEvent(new CartErrorEvent(null, typeof msg === 'string' ? msg : JSON.stringify(msg)));
        return;
      }

      // Dispatch with sections data — cart-drawer listens and morphs
      document.dispatchEvent(new CartAddEvent(data, null, {
        sections: data.sections,
        source: 'product-form',
      }));

      // Success animation on the button
      if (submitBtn) {
        submitBtn.classList.remove('is-loading');
        submitBtn.classList.add('is-added');
        setTimeout(() => {
          submitBtn.classList.remove('is-added');
          submitBtn.disabled = false;
        }, 2000);
      }
    } catch (err) {
      console.error('Product form error:', err);
      document.dispatchEvent(new CartErrorEvent(null, err.message));
      if (submitBtn) {
        submitBtn.classList.remove('is-loading');
        submitBtn.disabled = false;
      }
    }
  };
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}